"use client";

import React from "react";
import { Package, Truck, CheckCircle } from "lucide-react";
import StandardCard from "../common/StandardCard";
import { OrderSet } from "@/types/models/order";

interface OrderSetTrackingCardProps {
  orderSetId: string;
  orderSet?: OrderSet;
}

const OrderSetTrackingCard: React.FC<OrderSetTrackingCardProps> = ({
  orderSetId: _orderSetId,
  orderSet,
}) => {
  if (!orderSet) {
    return (
      <StandardCard title="Order Set Tracking">
        <div className="text-center text-sm text-muted-foreground py-8">
          Loading tracking information...
        </div>
      </StandardCard>
    );
  }

  const shippingStatus = orderSet.shipping_status;
  const shippingDetails = orderSet.shipping_details;

  // Define tracking stages
  const trackingStages = [
    {
      status: "PICKUP_QUEUED",
      label: "Order Placed",
      icon: Package,
      description: "Order set has been created",
    },
    {
      status: "READY_TO_SHIP",
      label: "Ready to Ship",
      icon: Package,
      description: "Order set is ready for pickup",
    },
    {
      status: "PICKED_UP",
      label: "Picked Up",
      icon: Truck,
      description: "Package picked up by courier",
    },
    {
      status: "IN_TRANSIT",
      label: "In Transit",
      icon: Truck,
      description: "Package is on the way",
    },
    {
      status: "OUT_FOR_DELIVERY",
      label: "Out for Delivery",
      icon: Truck,
      description: "Package is out for delivery",
    },
    {
      status: "DELIVERED",
      label: "Delivered",
      icon: CheckCircle,
      description: "Package has been delivered",
    },
  ];

  const getCurrentStageIndex = () => {
    const index = trackingStages.findIndex(
      (stage) => stage.status === shippingStatus,
    );
    return index >= 0 ? index : 0;
  };

  const currentStageIndex = getCurrentStageIndex();

  const isStageComplete = (index: number) => index < currentStageIndex;
  const isStageCurrent = (index: number) => index === currentStageIndex;

  return (
    <StandardCard title="Order Set Tracking">
      <div className="space-y-6">
        {/* Tracking Timeline */}
        <div className="space-y-4">
          {trackingStages.map((stage, index) => {
            const Icon = stage.icon;
            const isComplete = isStageComplete(index);
            const isCurrent = isStageCurrent(index);
            const isUpcoming = index > currentStageIndex;

            return (
              <div key={stage.status} className="flex gap-3">
                {/* Icon Column */}
                <div className="flex flex-col items-center">
                  <div
                    className={`
                      h-8 w-8 rounded-full flex items-center justify-center
                      ${isComplete ? "bg-green-100" : ""}
                      ${isCurrent ? "bg-blue-100" : ""}
                      ${isUpcoming ? "bg-gray-100" : ""}
                    `}
                  >
                    <Icon
                      className={`h-4 w-4
                        ${isComplete ? "text-green-600" : ""}
                        ${isCurrent ? "text-blue-600" : ""}
                        ${isUpcoming ? "text-gray-400" : ""}
                      `}
                    />
                  </div>
                  {index < trackingStages.length - 1 && (
                    <div
                      className={`w-0.5 h-12 mt-1
                        ${isComplete ? "bg-green-300" : "bg-gray-200"}
                      `}
                    />
                  )}
                </div>

                {/* Content Column */}
                <div className="flex-1 pb-4">
                  <div
                    className={`text-sm font-medium
                      ${isComplete ? "text-green-700" : ""}
                      ${isCurrent ? "text-blue-700" : ""}
                      ${isUpcoming ? "text-gray-500" : ""}
                    `}
                  >
                    {stage.label}
                  </div>
                  <div
                    className={`text-xs mt-1
                      ${isComplete || isCurrent ? "text-muted-foreground" : "text-gray-400"}
                    `}
                  >
                    {stage.description}
                  </div>
                  {isCurrent && shippingDetails?.courier_name && (
                    <div className="text-xs text-blue-600 mt-2 font-medium">
                      Courier: {shippingDetails.courier_name}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Courier Details */}
        {shippingDetails && (
          <div className="pt-4 border-t border-border space-y-2">
            <div className="text-sm font-medium text-foreground">
              Shipment Details
            </div>
            {shippingDetails.awb_code && (
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">AWB Code:</span>
                <span className="font-mono font-medium">
                  {shippingDetails.awb_code}
                </span>
              </div>
            )}
            {shippingDetails.tracking_url && (
              <a
                href={shippingDetails.tracking_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline block"
              >
                View detailed tracking
              </a>
            )}
          </div>
        )}
      </div>
    </StandardCard>
  );
};

export default OrderSetTrackingCard;
